/**
 * Car ground control — throttle, brake, steering, powerslide, tyre friction,
 * sticky force and the surface state machine.
 *
 * Sources: smish.dev/rocket_league/ground_control for the prose values, and
 * RocketSim / RLUtilities for the two curves the article only publishes as
 * images. Every number is imported from ./constants; see
 * docs/CONSTANTS_SOURCES.md §4 for provenance and the open questions.
 *
 * The model, in the order it is applied each tick:
 *
 *   1. four suspension raycasts (./suspension) -> contact count and normal
 *   2. surface transition: align the car's up axis to a NEW contact normal
 *   3. gravity + suspension springs
 *   4. sticky force, into the surface, along -normal
 *   5. throttle / brake / coast / reverse along the surface-tangential forward
 *   6. lateral tyre friction, which is what makes a normal turn feel planted
 *   7. steering, as a yaw rate about the surface normal (NOT a torque)
 *   8. integrate, clamp to CAR_MAX_SPEED, update the supersonic flag
 *
 * Two structural decisions worth stating up front, because neither is obvious
 * from the constants alone:
 *
 * STEERING IS KINEMATIC. `omega = curvature(speed) * speed * steer` is imposed
 * directly on the angular-velocity component along the surface normal. Rocket
 * League's published model is a *path curvature* table, not a tyre-force model,
 * so a torque formulation would need a slip model that no source publishes. The
 * two tangential components of omega are left alone, so the suspension still
 * owns pitch and roll — that is how the car keeps reacting to bumps and landings
 * while steering stays exact.
 *
 * SIGN. Positive steer turns LEFT. At identity orientation the surface normal is
 * +Z and the forward axis is +X, so a positive omega about +Z rotates forward
 * toward +Y, and +Y is the car's left in our right-handed frame. That is the
 * behavioural assertion docs/PHYSICS_REFERENCE.md §6.4 demands, and
 * `test/physics/ground.test.ts` asserts it explicitly rather than by inspection.
 */

import type { CarInput, CarState } from './car-state';
import {
  BOOST_ACCELERATION,
  BRAKE_ACCELERATION,
  BRAKING_NO_THROTTLE_SPEED_THRESH,
  CAR_MAX_ANGULAR_SPEED,
  CAR_MAX_SPEED,
  COAST_ACCELERATION,
  GRAVITY,
  HANDBRAKE_LAT_FRICTION_FACTOR,
  HANDBRAKE_LONG_FRICTION_FACTOR_CURVE,
  LAT_FRICTION_CURVE,
  LONG_FRICTION_FACTOR,
  NON_STICKY_FRICTION_FACTOR_CURVE,
  POWERSLIDE_FALL_RATE,
  POWERSLIDE_RISE_RATE,
  POWERSLIDE_STEER_ANGLE_CURVE,
  STEER_ANGLE_CURVE,
  STEER_CURVATURE_CURVE,
  STICKY_FORCE_BASE_SCALE,
  STOPPING_FORWARD_VEL,
  SUPERSONIC_MAINTAIN_MAX_TIME,
  SUPERSONIC_MAINTAIN_MIN_SPEED,
  SUPERSONIC_THRESHOLD,
  THROTTLE_ACCEL_CURVE,
  THROTTLE_AIRBORNE_WHEEL_PENALTY,
  THROTTLE_DEADZONE,
  WHEELS_FOR_GROUNDED,
} from './constants';
import {
  addScaledTo,
  axisToRotation,
  clamp,
  clampLengthBy,
  clone,
  cross,
  dot,
  forward,
  integrateRotation,
  length,
  mulMat,
  normalize,
  orthonormalize,
  piecewiseLinear,
  scale,
  set,
  sign,
  up,
  vec3,
  type Vec3,
} from './math';
import { DEFAULT_PHYSICS, type ResolvedPhysics } from './mutators';
import { classifySurface, type ArenaGeometry } from './surface';
import { computeSuspension, makeSuspensionResult, type SuspensionResult } from './suspension';

/**
 * The per-tick suspension buffer. One per module rather than one per car:
 * `stepGroundControl` consumes the result before it returns, so no two cars'
 * results are ever live at the same time, and a module-level scratch keeps the
 * hot path allocation-free without threading a handle through every caller.
 */
const _susp = makeSuspensionResult();

/** Scratch for `applyGroundForces`; see the note on `_susp`. */
const _fwd = vec3(0, 0, 0);
const _lft = vec3(0, 0, 0);
const _accel = vec3(0, 0, 0);

// ---------------------------------------------------------------------------
// Per-car controller state
// ---------------------------------------------------------------------------

/**
 * The analog powerslide value. RocketSim ramps the digital handbrake button
 * through POWERSLIDE_RISE_RATE / _FALL_RATE (0.2 s to engage, 0.5 s to release)
 * and that ramp is the ONLY time constant in the whole friction path — there is
 * no separate friction ramp-up constant in either simulator.
 *
 * It is `car.powerslide`. It used to be a module-level WeakMap keyed by the
 * CarState, which made a cloned or serialized car silently lose its handbrake
 * ramp; the two accessors below are what is left of that.
 */

/** Current analog powerslide value, 0..1. Exposed for tests and debug HUDs. */
export const powerslideValue = (car: CarState): number => car.powerslide;

/** Drop a car's controller state (start-of-match / respawn / test isolation). */
export const resetGroundController = (car: CarState): void => {
  car.powerslide = 0;
};

// ---------------------------------------------------------------------------
// Curves
// ---------------------------------------------------------------------------

/**
 * Forward acceleration available from full throttle at the given forward speed.
 * Reaches zero at CAR_MAX_THROTTLE_SPEED = 1410, which is why 1410 is a speed
 * the car approaches asymptotically rather than one it arrives at.
 */
export const throttleAccel = (forwardSpeed: number): number =>
  piecewiseLinear(THROTTLE_ACCEL_CURVE, Math.abs(forwardSpeed));

/** Maximum path curvature (1/uu) at the given speed. Turn radius = 1/curvature. */
export const steerCurvature = (speed: number): number =>
  piecewiseLinear(STEER_CURVATURE_CURVE, Math.abs(speed));

/**
 * Yaw rate about the surface normal, rad/s.
 *
 * The base model is `omega = curvature(v) * v * steer`. Powersliding is
 * published only in RocketSim's *wheel angle* parameterization, so it is
 * folded in as the ratio of the powerslide-blended steer angle to the normal
 * one — a dimensionless factor that is exactly 1 when the handbrake is off, so
 * the curvature model is untouched in the common case.
 *
 * ORDER IS LOAD-BEARING (docs/CONSTANTS_SOURCES.md §4): the handbrake blend
 * happens BEFORE the multiply by the steer input. Doing it the other way round
 * gives a different answer for every |steer| < 1.
 */
export const steerYawRate = (forwardSpeed: number, steer: number, powerslide: number): number => {
  const speed = Math.abs(forwardSpeed);
  const baseAngle = piecewiseLinear(STEER_ANGLE_CURVE, speed);
  let angle = baseAngle;
  if (powerslide > 0) {
    angle += (piecewiseLinear(POWERSLIDE_STEER_ANGLE_CURVE, speed) - angle) * powerslide;
  }
  angle *= clamp(steer, -1, 1);

  const ratio = baseAngle > 0 ? angle / baseAngle : 0;
  return steerCurvature(speed) * forwardSpeed * ratio;
};

// ---------------------------------------------------------------------------
// Supersonic
// ---------------------------------------------------------------------------

/**
 * RocketSim's exact branch structure. The timer accumulates over the WHOLE
 * supersonic period, not from the moment speed drops below the threshold, so
 * the maintain window is at most 1 s of being supersonic in total.
 */
export const updateSupersonic = (car: CarState, dt: number): void => {
  const speed = length(car.velocity);
  const threshold =
    car.supersonic && car.supersonicTimer < SUPERSONIC_MAINTAIN_MAX_TIME
      ? SUPERSONIC_MAINTAIN_MIN_SPEED
      : SUPERSONIC_THRESHOLD;

  car.supersonic = speed >= threshold;
  car.supersonicTimer = car.supersonic ? car.supersonicTimer + dt : 0;
};

// ---------------------------------------------------------------------------
// Surface transitions
// ---------------------------------------------------------------------------

/**
 * Rotate the car so its up axis matches `normal`, by the minimal rotation.
 * Called on floor -> wall -> ceiling transitions and on landing; within a
 * surface the suspension already tracks attitude, so this must NOT run every
 * tick or it would erase the 0.54-degree nose-down rest pitch.
 */
export const alignToSurface = (car: CarState, normal: Vec3): void => {
  const u = up(car.orientation);
  const c = clamp(dot(u, normal), -1, 1);
  if (c > 1 - 1e-12) return;

  // Flipping exactly upside down has no unique minimal axis; roll about the
  // car's own forward, which keeps the heading.
  const axis = c < -1 + 1e-9 ? forward(car.orientation) : normalize(cross(u, normal));
  const delta = axisToRotation(scale(axis, Math.acos(c)));
  car.orientation = orthonormalize(mulMat(delta, car.orientation));
};

// ---------------------------------------------------------------------------
// The tick
// ---------------------------------------------------------------------------

/**
 * Advance one car by one tick of ground control.
 *
 * Owns gravity, linear integration and — while any wheel is in contact —
 * orientation integration. While fully airborne it applies gravity and moves
 * the car but leaves rotation to `stepAerialRotation`, so exactly one system
 * integrates the orientation at any moment. A caller that would rather own the
 * integration outright passes `integrateOrientationFlag: false`; the angular
 * VELOCITY contributions from the springs and from steering still happen.
 *
 * Boost: the ground thrust (BOOST_ACCELERATION) is applied here because it is a
 * driving force along the surface, but the boost RESOURCE is not touched — tank
 * accounting belongs to ./boost. A caller running both must let ./boost decide
 * whether boost is actually active this tick.
 */
export interface GroundStepOptions {
  /**
   * Apply boost thrust here. Set false when ./boost owns thrust for this car —
   * otherwise both modules add BOOST_ACCELERATION and the car accelerates at
   * twice the correct rate.
   */
  applyBoostThrust?: boolean;
  /**
   * Update the supersonic flag here. Set false when ./boost owns it. Calling
   * both in one tick advances `supersonicTimer` by 2*dt, which halves the
   * hysteresis window.
   */
  updateSupersonicFlag?: boolean;
  /**
   * Advance the orientation matrix here. Set false when the caller integrates
   * rotation itself — the angular VELOCITY updates (suspension torque and
   * steering) still happen, only the `Theta += omega * dt` step is skipped.
   *
   * A caller that also runs ./aerial needs this: while one or two wheels touch,
   * this module integrates but `stepJump` still reports air control, so both
   * would advance the same orientation in the same tick. With the flag off the
   * world owns the single integration and the "exactly once per tick" rule is
   * structural rather than a case analysis.
   */
  integrateOrientationFlag?: boolean;
  /**
   * The world's resolved mutator values (see ./mutators). Defaults to
   * `DEFAULT_PHYSICS`, whose fields hold exactly the constants this module used
   * to read directly.
   *
   * Only GRAVITY varies here, and only where it is really gravity — the sticky
   * force does NOT scale with it. See the note at its application site.
   */
  physics?: ResolvedPhysics;
}

export const stepGroundControl = (
  car: CarState,
  input: CarInput,
  arena: ArenaGeometry,
  dt: number,
  options: GroundStepOptions = {}
): void => {
  const applyBoostThrust = options.applyBoostThrust ?? true;
  const doSupersonic = options.updateSupersonicFlag ?? true;
  const doIntegrateOrientation = options.integrateOrientationFlag ?? true;
  const physics = options.physics ?? DEFAULT_PHYSICS;

  // Analog handbrake ramp — the only time constant in the friction path.
  const rate = input.handbrake ? POWERSLIDE_RISE_RATE : POWERSLIDE_FALL_RATE;
  const target = input.handbrake ? 1 : 0;
  const step = rate * dt;
  car.powerslide =
    Math.abs(target - car.powerslide) <= step
      ? target
      : car.powerslide + sign(target - car.powerslide) * step;

  const susp = computeSuspension(car, arena, _susp);

  if (susp.contactCount === 0 || susp.normal === null) {
    leaveSurface(car);
    car.velocity.z -= physics.gravity * dt;
    clampLengthBy(car.velocity, CAR_MAX_SPEED);
    addScaledTo(car.position, car.velocity, dt);
    if (doSupersonic) updateSupersonic(car, dt);
    return;
  }

  const normal = susp.normal;
  const grounded = susp.contactCount >= WHEELS_FOR_GROUNDED;
  const category = classifySurface(normal);

  // A transition is either a landing or a change of surface class. Inside one
  // surface the suspension tracks the geometry on its own.
  if (grounded && (!car.onGround || car.surface !== category)) {
    alignToSurface(car, normal);
    // Tangential spin carried in from the air would immediately tip the car off
    // the surface it just landed on; keep only the spin about the new normal.
    car.angularVelocity = scale(normal, dot(car.angularVelocity, normal));
  }

  car.surfaceNormal = clone(normal);
  car.onGround = grounded;
  car.surface = grounded ? category : 'none';

  applyGroundForces(
    car,
    input,
    susp,
    normal,
    grounded,
    car.powerslide,
    dt,
    applyBoostThrust,
    doIntegrateOrientation,
    physics
  );
  if (doSupersonic) updateSupersonic(car, dt);
};

/** Mark the car airborne. `surfaceNormal` keeps its last value as a hint. */
const leaveSurface = (car: CarState): void => {
  car.onGround = false;
  car.surface = 'none';
};

const applyGroundForces = (
  car: CarState,
  input: CarInput,
  susp: SuspensionResult,
  normal: Vec3,
  grounded: boolean,
  powerslide: number,
  dt: number,
  applyBoostThrust: boolean,
  doIntegrateOrientation: boolean,
  physics: ResolvedPhysics
): void => {
  // Surface-tangential basis. `left = normal x forward` reproduces the +Y-is-left
  // column of the orientation matrix at identity (see the sign note at the top).
  // Scratch, not fresh vectors: this runs for every live car every tick.
  const m = car.orientation;
  const fd = m[0] * normal.x + m[3] * normal.y + m[6] * normal.z;
  set(_fwd, m[0] - normal.x * fd, m[3] - normal.y * fd, m[6] - normal.z * fd);
  const fl = Math.sqrt(_fwd.x * _fwd.x + _fwd.y * _fwd.y + _fwd.z * _fwd.z);
  if (fl < 1e-9) set(_fwd, 0, 0, 0);
  else set(_fwd, _fwd.x / fl, _fwd.y / fl, _fwd.z / fl);
  const fwd = _fwd;

  set(
    _lft,
    normal.y * fwd.z - normal.z * fwd.y,
    normal.z * fwd.x - normal.x * fwd.z,
    normal.x * fwd.y - normal.y * fwd.x
  );
  const lft = _lft;

  const vForward = dot(car.velocity, fwd);
  const vLateral = dot(car.velocity, lft);
  const speed = length(car.velocity);

  const accel = set(_accel, 0, 0, -physics.gravity);
  addScaledTo(accel, susp.linearAccel, 1);

  // Boosting forces full throttle. Verbatim from RocketSim `Car::_UpdateDriving`:
  //
  //     float realThrottle = controls.throttle;
  //     if (controls.boost && _internalState.boost > 0)
  //         realThrottle = 1;
  //
  // It is applied BEFORE the deadzone test, so a boosting car can never take
  // the coast branch. This is load-bearing: without it, boosting with no
  // throttle applies 525 uu/s^2 of coast brake against the boost thrust and the
  // car barely accelerates — but in the real game, boost alone takes you to
  // supersonic. `realThrottle` also feeds the sticky-force and grip terms
  // below, exactly as it does in the source.
  // The `physics.boostEnabled` term is the NO BOOST mutator: with it off the
  // tank is pinned at 0 anyway, but a caller that writes a tank level by hand
  // must not get free full throttle out of a button that produces no thrust.
  const realThrottle =
    physics.boostEnabled && input.boost && car.boostAmount > 0 ? 1 : input.throttle;

  const throttling = Math.abs(realThrottle) >= THROTTLE_DEADZONE;

  // --- sticky force ------------------------------------------------------
  // scale = 0.5 + (fullStick ? 1 - |n.z| : 0), directed into the surface.
  // 325 uu/s^2 on floor and ceiling, up to 975 on a vertical wall.
  //
  // NOTE it is the SOURCED GRAVITY, not `physics.gravity`: the gravity mutator
  // does not scale this. RocketSim expresses the sticky force in units of
  // standard gravity purely as a way of writing 325, but it is a surface
  // adhesion force, not weight — it is what holds a car to a wall or a ceiling,
  // where weight is doing nothing for it. Scaling it would make LOW gravity
  // slide cars off walls and would make REVERSE gravity, whose whole point is
  // driving on the ceiling, eject every car from every surface instead. That is
  // a deliberate model decision, not an oversight; flipping it is one symbol.
  const fullStick = throttling || Math.abs(vForward) > STOPPING_FORWARD_VEL;
  const stickScale = STICKY_FORCE_BASE_SCALE + (fullStick ? 1 - Math.abs(normal.z) : 0);
  addScaledTo(accel, normal, -stickScale * GRAVITY);

  // --- friction factors --------------------------------------------------
  // A wheel that is not touching cannot generate grip; scaling by the contact
  // fraction is the per-wheel model, not a tunable.
  // `susp.wheels.length` rather than a constant 4: it is the same four wheels
  // for every body (./car-config's `threeWheels` is a rendering fact) and this
  // way the grip fraction cannot disagree with what was actually cast.
  const grip = susp.contactCount / susp.wheels.length;
  const slip = speed > 1e-6 ? clamp(Math.abs(vLateral) / speed, 0, 1) : 0;

  // With no throttle the contact is not "sticky" and grip collapses with the
  // contact normal's Z. This is exactly why a coasting car slides off a wall.
  const nonSticky = throttling
    ? 1
    : piecewiseLinear(NON_STICKY_FRICTION_FACTOR_CURVE, Math.abs(normal.z));

  const longFactor =
    LONG_FRICTION_FACTOR *
    ((piecewiseLinear(HANDBRAKE_LONG_FRICTION_FACTOR_CURVE, slip) - 1) * powerslide + 1) *
    nonSticky *
    grip;

  const latFactor =
    piecewiseLinear(LAT_FRICTION_CURVE, slip) *
    ((HANDBRAKE_LAT_FRICTION_FACTOR - 1) * powerslide + 1) *
    nonSticky *
    grip;

  // --- throttle / brake / coast / reverse --------------------------------
  //
  // [DEVIATION, deliberate and load-bearing for the RL baselines — see below.]
  // RocketSim `Car::_UpdateDriving` keeps the BRAKE and the ENGINE as two
  // separate wheel forces, and its two speed thresholds gate different things:
  //
  //     if (|throttle| >= THROTTLE_DEADZONE) {
  //         if (|v| > STOPPING_FORWARD_VEL && sgn(throttle) != sgn(v)) {
  //             realBrake = 1;
  //             if (|v| > BRAKING_NO_THROTTLE_SPEED_THRESH) engineThrottle = 0;
  //         }
  //     } else { ... }
  //
  // Braking at all is gated on STOPPING_FORWARD_VEL (25 uu/s); the 0.01 uu/s
  // BRAKING_NO_THROTTLE_SPEED_THRESH only decides whether the engine is
  // silenced on top of a brake already being applied, and is inert because 0.01
  // is far below 25. WE GATE BRAKING ON THE 0.01 INSTEAD, and that has a real
  // consequence: a car nosed a couple of degrees uphill keeps a few hundredths
  // of a uu/s of backward gravity drift, which always clears 0.01 and never
  // clears 25, so full throttle is read as braking, the clamp below removes
  // exactly the drift and nothing more, and the car sits there under power
  // indefinitely. It is invisible on flat ground and permanent on the floor
  // fillet at the foot of a wall.
  //
  // Swapping the two thresholds is a one-symbol change and it is CORRECT, but
  // it moves the low-speed dynamics enough to invalidate two calibrated
  // learning thresholds in test/rl/trainer.test.ts (the touch-rate trend on the
  // pipeline seed collapses from 3x to 1.2x, and the curriculum's held-out
  // return gain from +0.44 to +0.04). Those bars were measured against THIS
  // behaviour, and re-deriving them is a decision about the RL baselines rather
  // than part of a bot fix, so the change is left for that call to be made
  // deliberately. Until then the bot layer recovers from the pose on its own —
  // `unstick` in ../bot/mechanics.ts — and test/bot/liveness.test.ts holds the
  // guarantee that it does.
  const wheelPenalty = grounded ? 1 : THROTTLE_AIRBORNE_WHEEL_PENALTY;
  let drive: number;

  if (!throttling) {
    // Coasting. Below STOPPING_FORWARD_VEL the FULL brake is applied instead of
    // the 0.15 coast factor, which is what actually brings the car to a stop.
    const decel =
      (Math.abs(vForward) > STOPPING_FORWARD_VEL ? COAST_ACCELERATION : BRAKE_ACCELERATION) *
      longFactor;
    drive = -sign(vForward) * Math.min(decel, Math.abs(vForward) / dt);
  } else if (
    realThrottle * vForward < 0 &&
    Math.abs(vForward) > BRAKING_NO_THROTTLE_SPEED_THRESH
  ) {
    // Throttle opposes motion: pure brake, engine throttle forced to zero.
    // The clamp keeps the brake from pulling the car past zero — a
    // constraint-solved brake cannot reverse a velocity and a plain
    // `-3500 * dt` would.
    const decel = BRAKE_ACCELERATION * longFactor;
    drive = -sign(vForward) * Math.min(decel, Math.abs(vForward) / dt);
  } else {
    drive = realThrottle * throttleAccel(vForward) * wheelPenalty;
  }
  addScaledTo(accel, fwd, drive);

  if (applyBoostThrust && physics.boostEnabled && input.boost && car.boostAmount > 0) {
    addScaledTo(accel, fwd, BOOST_ACCELERATION);
  }

  // --- lateral tyre friction ---------------------------------------------
  // Removes `latFactor` of the sideways slip within this tick. At zero slip the
  // curve gives 1 (fully planted); at full sideways slide it gives 0.2, and the
  // handbrake multiplies it by 0.1 on top, which is the powerslide.
  if (dt > 0) addScaledTo(accel, lft, (-vLateral * latFactor) / dt);

  // --- integrate ----------------------------------------------------------
  addScaledTo(car.velocity, accel, dt);
  clampLengthBy(car.velocity, CAR_MAX_SPEED);
  addScaledTo(car.position, car.velocity, dt);

  // Suspension torque drives pitch and roll; steering overrides the spin about
  // the surface normal only.
  addScaledTo(car.angularVelocity, susp.angularAccel, dt);
  if (grounded) {
    const omega = steerYawRate(vForward, input.steer, powerslide);
    // Keep the spin off the surface normal, replace the spin about it.
    const w = car.angularVelocity;
    const along = w.x * normal.x + w.y * normal.y + w.z * normal.z;
    w.x += normal.x * (omega - along);
    w.y += normal.y * (omega - along);
    w.z += normal.z * (omega - along);
  }
  clampLengthBy(car.angularVelocity, CAR_MAX_ANGULAR_SPEED);

  // In place, and bit-identical to the allocating form — see `integrateRotation`.
  if (doIntegrateOrientation) integrateRotation(car.orientation, car.angularVelocity, dt);
};
